import { useState, lazy, Suspense } from 'react';
const ReportModal = lazy(() => import('../moderation/ReportModal'));
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { resolveMedia } from '../social/discovery';
import MediaLightbox from './MediaLightbox';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Bookmark, 
  MoreHorizontal,
  Repeat2,
  Trash2,
  Flag,
  Send,
  Pencil,
  Quote,
  BadgeCheck,
  MapPin,
  Sparkles,
  Play
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import DonationButton from './DonationButton';
import ShareMenu from '../share/ShareMenu';

export function PostCard({ post, onUpdate, onDelete, onBookmarkRemove, showFullContent = false, showComments = false }) {
  const { user } = useAuth();
  const [likeLoading, setLikeLoading] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [repostLoading, setRepostLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [editLoading, setEditLoading] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteContent, setQuoteContent] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [showCommentsSection, setShowCommentsSection] = useState(showComments);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);

  const navigate = useNavigate();
  const isOwnPost = user?.id === post.user_id;

  // Optional canonical context — rendered only when the real data exists.
  const realmLabel = post.realm || post.user?.realm || null;
  const locationLabel = post.location || post.world_name || null;
  const contextLabel = locationLabel || realmLabel;
  const isVerified = post.user?.is_verified;
  const isNpc = post.user?.is_npc;

  // Media items for the clickable lightbox viewer.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [commentReportId, setCommentReportId] = useState(null);
  const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i;
  const itemType = (url) => {
    if (post.media_type === 'video') return 'video';
    if (post.media_type === 'image') return 'image';
    return VIDEO_EXT.test(url) ? 'video' : 'image'; // mixed/unknown -> infer
  };
  const mediaItems = (post.media_urls || []).map((u) => ({ url: resolveMedia(u), type: itemType(u) }));
  const openLightbox = (i) => { setLightboxIndex(i); setLightboxOpen(true); };

  const handleLike = async () => {
    if (likeLoading) return;
    setLikeLoading(true);

    try {
      if (post.is_liked) {
        await api.delete(`/posts/${post.id}/like`);
        onUpdate?.(post.id, { is_liked: false, likes_count: post.likes_count - 1 });
      } else {
        await api.post(`/posts/${post.id}/like`);
        onUpdate?.(post.id, { is_liked: true, likes_count: post.likes_count + 1 });
      }
    } catch (error) {
      toast.error('Failed to update like');
    } finally {
      setLikeLoading(false);
    }
  };

  const handleBookmark = async () => {
    if (bookmarkLoading) return;
    setBookmarkLoading(true);

    try {
      if (post.is_bookmarked) {
        await api.delete(`/posts/${post.id}/bookmark`);
        onUpdate?.(post.id, { is_bookmarked: false, bookmarks_count: post.bookmarks_count - 1 });
        onBookmarkRemove?.(post.id);
        toast.success('Removed from bookmarks');
      } else {
        await api.post(`/posts/${post.id}/bookmark`);
        onUpdate?.(post.id, { is_bookmarked: true, bookmarks_count: post.bookmarks_count + 1 });
        toast.success('Added to bookmarks');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    } finally {
      setBookmarkLoading(false);
    }
  };

  const handleRepost = async () => {
    if (repostLoading || post.is_reposted) return;
    setRepostLoading(true);

    try {
      await api.post(`/posts/${post.id}/repost`);
      onUpdate?.(post.id, { is_reposted: true, reposts_count: post.reposts_count + 1 });
      toast.success('Reposted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repost');
    } finally {
      setRepostLoading(false);
    }
  };

  const handleQuote = async () => {
    if (quoteLoading || !quoteContent.trim()) return;
    setQuoteLoading(true);
    try {
      await api.post('/posts', { content: quoteContent.trim(), quoted_post_id: post.id });
      setQuoteOpen(false);
      setQuoteContent('');
      toast.success('Shared to your profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setQuoteLoading(false);
    }
  };

  const startEdit = () => { setEditContent(post.content || ''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setEditContent(post.content || ''); };

  const handleEdit = async () => {
    if (editLoading || !editContent.trim()) return;
    setEditLoading(true);
    try {
      const res = await api.put(`/posts/${post.id}`, { content: editContent.trim() });
      onUpdate?.(post.id, { content: res.data.content, hashtags: res.data.hashtags, edited_at: res.data.edited_at });
      setEditing(false);
      toast.success('Post updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await api.delete(`/posts/${post.id}`);
      onDelete?.(post.id);
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const loadComments = async () => {
    if (commentsLoaded) return;
    try {
      const response = await api.get(`/posts/${post.id}/comments`);
      setComments(response.data);
      setCommentsLoaded(true);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleToggleComments = () => {
    if (!showCommentsSection) {
      loadComments();
    }
    setShowCommentsSection(!showCommentsSection);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || commentLoading) return;

    setCommentLoading(true);
    try {
      const response = await api.post(`/posts/${post.id}/comments`, {
        content: newComment.trim()
      });
      setComments(prev => [response.data, ...prev]);
      setNewComment('');
      onUpdate?.(post.id, { comments_count: post.comments_count + 1 });
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleShare = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <article 
      className="mg-post-card overflow-hidden feed-post animate-fadeIn"
      data-testid={`post-${post.id}`}
    >
      {/* Header */}
      <div className="p-4 flex items-start justify-between gap-3">
        <Link 
          to={`/profile/${post.user?.username}`}
          className="flex items-center gap-3 min-w-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          data-testid="post-author-link"
        >
          <Avatar className="w-11 h-11 border border-primary/40 shadow-[0_0_14px_-4px_hsl(var(--mg-purple)/0.6)] shrink-0">
            <AvatarImage src={post.user?.avatar} />
            <AvatarFallback>{post.user?.display_name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-semibold hover:underline truncate" data-testid="post-author-name">
                {post.user?.display_name}
              </p>
              {isVerified && (
                <BadgeCheck 
                  className="w-4 h-4 text-primary shrink-0" 
                  aria-label="Verified"
                  data-testid="post-verified-badge"
                />
              )}
              {isNpc && (
                <span className="mg-npc-badge" data-testid="post-npc-badge">
                  <Sparkles className="w-3 h-3" aria-hidden="true" />
                  Myth
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground flex-wrap">
              {post.user?.username && <span className="truncate">@{post.user.username}</span>}
              <span aria-hidden="true">·</span>
              <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
              {contextLabel && (
                <span className="mg-realm-badge ml-1" data-testid="post-realm-badge">
                  <MapPin className="w-3 h-3" aria-hidden="true" />
                  {contextLabel}
                </span>
              )}
            </div>
          </div>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full shrink-0" data-testid="post-menu-btn">
              <MoreHorizontal className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-border">
            {isOwnPost ? (
              <>
                <DropdownMenuItem onClick={startEdit} data-testid="edit-post-btn">
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Post
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className="text-destructive" data-testid="delete-post-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Post
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="report-post-btn">
                <Flag className="w-4 h-4 mr-2" />
                Report Post
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content — clickable to open post detail */}
      <div
        className={`px-4 pb-3${!showFullContent && !editing ? ' cursor-pointer' : ''}`}
        onClick={(e) => {
          if (showFullContent || editing) return;
          if (e.target.closest('a, button, [role="button"], textarea, input')) return;
          navigate(`/post/${post.id}`);
        }}
        data-testid="post-content-area"
      >
        {editing ? (
          <div className="space-y-2" data-testid="post-edit-area">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={4}
              className="resize-none"
              data-testid="post-edit-input"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={editLoading} className="rounded-full" data-testid="post-edit-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={handleEdit} disabled={editLoading || !editContent.trim()} className="rounded-full" data-testid="post-edit-save">
                {editLoading ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className={`whitespace-pre-wrap [overflow-wrap:anywhere] ${showFullContent ? '' : 'line-clamp-4'}`}>
              {post.content}
            </p>
            {post.edited_at && (
              <span className="text-xs text-muted-foreground" data-testid="post-edited-tag">(edited)</span>
            )}
          </>
        )}
        
        {/* Hashtags */}
        {post.hashtags?.length > 0 && !editing && (
          <div className="flex flex-wrap gap-2 mt-2">
            {post.hashtags.map(tag => (
              <Link 
                key={tag}
                to={`/search?q=${tag}`}
                className="text-primary hover:underline text-sm"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}

        {/* Quoted (shared) post */}
        {post.quoted_post && (
          <Link
            to={`/post/${post.quoted_post.id}`}
            className="mt-3 block rounded-2xl border border-border hover:border-primary/40 transition-colors p-3 bg-muted/30"
            data-testid="quoted-post"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Avatar className="w-6 h-6">
                <AvatarImage src={resolveMedia(post.quoted_post.user?.avatar)} className="object-cover" />
                <AvatarFallback>{post.quoted_post.user?.display_name?.[0]}</AvatarFallback>
              </Avatar>
              <span className="font-semibold text-sm truncate">{post.quoted_post.user?.display_name}</span>
              {post.quoted_post.user?.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0" />}
              <span className="text-xs text-muted-foreground truncate">@{post.quoted_post.user?.username}</span>
            </div>
            <p className="text-sm text-foreground/90 line-clamp-3 whitespace-pre-wrap">{post.quoted_post.content}</p>
            {post.quoted_post.media_urls?.length > 0 && (
              <div className="mt-2 rounded-xl overflow-hidden aspect-video bg-muted">
                <img src={resolveMedia(post.quoted_post.media_urls[0])} alt="" className="w-full h-full object-cover" />
              </div>
            )}
          </Link>
        )}
      </div>

      {/* Media */}
      {mediaItems.length > 0 && (
        <div className="px-4 pb-4">
          {mediaItems.length === 1 ? (
            /* Single media — whole image at its own aspect ratio (portraits letterboxed, never cropped) */
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="mg-media-frame relative block w-full overflow-hidden rounded-xl group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              data-testid="post-media-0"
              aria-label={mediaItems[0].type === 'video' ? 'Play video' : 'View image'}
            >
              {mediaItems[0].type === 'video' ? (
                <>
                  <video
                    src={mediaItems[0].url}
                    className="block w-full h-auto max-h-[min(600px,75svh)] object-contain bg-black/40 pointer-events-none"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors pointer-events-none">
                    <span className="w-14 h-14 rounded-full bg-black/55 flex items-center justify-center">
                      <Play className="w-7 h-7 text-white translate-x-0.5" />
                    </span>
                  </span>
                </>
              ) : (
                <img
                  src={mediaItems[0].url}
                  alt=""
                  className="block w-full h-auto max-h-[min(600px,75svh)] object-contain bg-black/40 pointer-events-none"
                />
              )}
            </button>
          ) : (
            /* Multi media — grid with consistent square cells */
            <div className="grid grid-cols-2 gap-2">
              {mediaItems.map((item, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="mg-media-frame relative block w-full aspect-square overflow-hidden rounded-xl group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  data-testid={`post-media-${index}`}
                  aria-label={item.type === 'video' ? 'Play video' : 'View image'}
                >
                  {item.type === 'video' ? (
                    <>
                      <video
                        src={item.url}
                        className="w-full h-full object-cover pointer-events-none"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors pointer-events-none">
                        <span className="w-14 h-14 rounded-full bg-black/55 flex items-center justify-center">
                          <Play className="w-7 h-7 text-white translate-x-0.5" />
                        </span>
                      </span>
                    </>
                  ) : (
                    <img
                      src={item.url}
                      alt=""
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <MediaLightbox
        open={lightboxOpen}
        items={mediaItems}
        startIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Actions */}
      <div className="px-4 py-3 border-t border-[hsl(var(--mg-rune)/0.22)] flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLike}
            disabled={likeLoading}
            className={`rounded-full gap-2 ${post.is_liked ? 'text-red-500' : ''}`}
            data-testid="like-btn"
            aria-label={post.is_liked ? 'Unlike post' : 'Like post'}
          >
            <Heart className={`w-5 h-5 ${post.is_liked ? 'fill-current' : ''}`} />
            <span className="text-sm">{post.likes_count || ''}</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleComments}
            className="rounded-full gap-2"
            data-testid="comment-btn"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm">{post.comments_count || ''}</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={repostLoading}
                className={`rounded-full gap-2 ${post.is_reposted ? 'text-green-500' : ''}`}
                data-testid="repost-btn"
              >
                <Repeat2 className="w-5 h-5" />
                <span className="text-sm">{post.reposts_count || ''}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-card border-border">
              <DropdownMenuItem onClick={handleRepost} disabled={post.is_reposted} data-testid="repost-simple-btn">
                <Repeat2 className="w-4 h-4 mr-2" />
                {post.is_reposted ? 'Reposted' : 'Repost'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setQuoteContent(''); setQuoteOpen(true); }} data-testid="repost-quote-btn">
                <Quote className="w-4 h-4 mr-2" />
                Quote (add your words)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-1">
          {/* Donation Button */}
          {!isOwnPost && (
            <DonationButton 
              recipientId={post.user_id}
              recipientName={post.user?.display_name}
              variant="compact"
            />
          )}

          <ShareMenu postId={post.id} text={post.content} testId="post-share-menu">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" />
            </Button>
          </ShareMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleBookmark}
            disabled={bookmarkLoading}
            className={`rounded-full ${post.is_bookmarked ? 'text-primary' : ''}`}
            data-testid="bookmark-btn"
          >
            <Bookmark className={`w-5 h-5 ${post.is_bookmarked ? 'fill-current' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Comments Section */}
      {showCommentsSection && (
        <div className="border-t border-[hsl(var(--mg-rune)/0.22)] p-4 space-y-4">
          {/* Comment Input */}
          <form onSubmit={handleSubmitComment} className="flex gap-2">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback>{user?.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <Input
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="flex-1 rounded-full bg-muted/50 border-transparent h-9"
              data-testid="comment-input"
            />
            <Button 
              type="submit" 
              size="sm"
              disabled={!newComment.trim() || commentLoading}
              className="rounded-full h-9 w-9 p-0"
              data-testid="submit-comment-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>

          {/* Comments List */}
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {comments.map(comment => (
              <div key={comment.id} className="flex gap-3">
                <Link to={`/profile/${comment.user?.username}`}>
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={comment.user?.avatar} />
                    <AvatarFallback>{comment.user?.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="bg-muted/50 rounded-2xl px-3 py-2">
                    <Link
                      to={`/profile/${comment.user?.username}`}
                      className="font-semibold text-sm hover:underline"
                    >
                      {comment.user?.display_name}
                    </Link>
                    <p className="text-sm">{comment.content}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-1 ml-3">
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </p>
                    {comment.user?.id !== user?.id && (
                      <button
                        onClick={() => setCommentReportId(comment.id)}
                        className="text-xs text-muted-foreground/60 hover:text-destructive transition-colors flex items-center gap-0.5"
                        aria-label="Report comment"
                        data-testid={`report-comment-${comment.id}`}
                      >
                        <Flag className="w-3 h-3" />
                        <span>Report</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-4">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>
      )}
      {/* Quote / Share-with-words dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-lg bg-card border-primary/20" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle>Share with your words</DialogTitle>
          </DialogHeader>
          <Textarea
            value={quoteContent}
            onChange={(e) => setQuoteContent(e.target.value)}
            placeholder="Add a comment…"
            rows={3}
            className="resize-none"
            data-testid="quote-input"
            autoFocus
          />
          <div className="rounded-2xl border border-border p-3 bg-muted/30">
            <div className="flex items-center gap-2 mb-1">
              <Avatar className="w-6 h-6">
                <AvatarImage src={resolveMedia(post.user?.avatar)} className="object-cover" />
                <AvatarFallback>{post.user?.display_name?.[0]}</AvatarFallback>
              </Avatar>
              <span className="font-semibold text-sm truncate">{post.user?.display_name}</span>
              <span className="text-xs text-muted-foreground truncate">@{post.user?.username}</span>
            </div>
            <p className="text-sm text-foreground/90 line-clamp-3 whitespace-pre-wrap">{post.content}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuoteOpen(false)} disabled={quoteLoading} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleQuote} disabled={quoteLoading || !quoteContent.trim()} className="rounded-full" data-testid="quote-submit">
              {quoteLoading ? 'Sharing…' : 'Share'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Suspense fallback={null}>
        <ReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          targetType="post"
          targetId={post.id}
          targetLabel="post"
        />
      </Suspense>

      <Suspense fallback={null}>
        <ReportModal
          open={!!commentReportId}
          onClose={() => setCommentReportId(null)}
          targetType="comment"
          targetId={commentReportId || ''}
          targetLabel="comment"
        />
      </Suspense>
    </article>
  );
}

export default PostCard;
